import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {
    selectBlockTimesWithCurrentTimeFromStates,
    selectButtonOnLastScene,
    selectCurrentSceneFromState,
    selectNavigateToTimeFromState,
    selectVideoClipForTimeFromState
} from '../stores/root-store/selectors/video.selector';
import {
    filter, first, flatMap, map, mergeMap
} from 'rxjs/operators';
import {combineLatest} from 'rxjs';
import {
    ChangeClipByTimeAction,
    SetInitialVideoValues,
    SetStartSceneTimeAction,
    SetVideoStateTypeAction,
    UpdateNavigateTimeAction
} from '../stores/root-store/actions/video.action';
import VideoState from '../stores/root-store/video.state';
import {VideoStateEnum} from '../../../enums/video-state.enum';
import {BrowserService} from './browser.service';

@Injectable()
export class NavigationService {
    constructor(private store: Store<{ video: VideoState }>, private browserService: BrowserService) {
    }

    navigateToTime(payload) {
        return this.store.select(selectBlockTimesWithCurrentTimeFromStates)
            .pipe(
                first(),
                mergeMap(({breakPointsList, currentTime}) => this.navigateAndChangeClipAction(payload, currentTime, breakPointsList))
            );
    }

    changeClipByNavigateTime(navTime) {
        return combineLatest(
            this.store.select(selectVideoClipForTimeFromState, navTime),
            this.store.select(selectCurrentSceneFromState)
        ).pipe(
            first(),
            map(([{clipFound}, currentScene]) => {
                if (clipFound !== currentScene) {
                    this.clipChangeAction(clipFound);
                }

                return UpdateNavigateTimeAction({
                    navigationTime: navTime
                });
            })
        );
    }

    jumpTo(timeToJump, videoState: VideoStateEnum) {
        return this.store.select(selectNavigateToTimeFromState).pipe(first(), map((navigationTime) => {
            let isButtonOnLastScene;
            this.store.select(selectButtonOnLastScene).pipe(
                first(),
                map(button => isButtonOnLastScene = !!button))
                .subscribe();
            if (videoState === VideoStateEnum.Ended && isButtonOnLastScene) {
                videoState = VideoStateEnum.Playing;
            }

            this.store.dispatch(SetVideoStateTypeAction({
                videoState
            }));
            let time = timeToJump;

            // IPhones and IPads have difficulties to seek video with time that has more then one digit after comma
            if (this.browserService.isAppleMobileDevice()) {
                time = Math.ceil(timeToJump * 10) / 10;
                time += 0.1;
            }

            return ChangeClipByTimeAction({
                navigationTime: navigationTime === time ? time + 0.0001 : time
            });
        }));
    }

    private clipChangeAction(clipFound) {
        this.store.dispatch(SetInitialVideoValues(
            {
                currentScene: clipFound
            }));
    }

    private navigateAndChangeClipAction(payload, currentTime, breakPointsList) {
        const time = this.checkBreakPointWhenUserWantNavigate(payload.navigationTime, currentTime, breakPointsList);

        return this.jumpTo(time, time !== payload.navigationTime ? VideoStateEnum.Interaction : payload.videoState);
    }

    private checkBreakPointWhenUserWantNavigate(navTime, currentTime, breakPointList) {
        if (currentTime > navTime) {
            return navTime;
        }
        const breakPoint = breakPointList.find((breakTime) => currentTime < breakTime && navTime > breakTime);
        return breakPoint ? breakPoint - 1 : navTime;
    }

    setTimeToStartScene(videoState) {
        let currentVideo;
        this.store.dispatch(SetStartSceneTimeAction({
            startSceneTime: 0
        }));

        return this.store.select(selectCurrentSceneFromState)
            .pipe(
                map(video => currentVideo = video),
                filter(state => videoState !== VideoStateEnum.Ended),
                flatMap(() => this.store.select(selectNavigateToTimeFromState)),
                first(),
                filter(time => time && time !== 0),
                map(time => time - currentVideo.time.start)
            );
    }
}
